import React from 'react';
import { Box, Container, Typography, Button, Grid, alpha, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VerifiedIcon from '@mui/icons-material/Verified';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

const HeroSection = ({ onLoginClick, onRequestDemo }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const features = [
    t('peppol_compliant'),
    t('inventory_tracking'),
    t('integrated_pos'),
    t('multi_store'),
  ];

  return (
    <Box
      sx={{
        minHeight: { xs: 'auto', md: '60vh' }, // Reduced height, auto on mobile
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 3, sm: 4, md: 5 }, // Controlled vertical padding
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(theme.palette.secondary.main, 0.22)} 50%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.25)} 0%, transparent 70%)`,
          animation: 'float 20s ease-in-out infinite',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-30%',
          left: '-10%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.20)} 0%, transparent 70%)`,
          animation: 'float 15s ease-in-out infinite reverse',
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0) translateX(0)' },
          '50%': { transform: 'translateY(-30px) translateX(20px)' },
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 1, sm: 2 }, py: 0 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                animation: 'fadeInUp 1s ease-out',
                '@keyframes fadeInUp': {
                  '0%': { opacity: 0, transform: 'translateY(30px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  fontSize: '0.72rem',
                  mb: 1,
                  display: 'block',
                }}
              >
                PEPPOL COMPLIANT • MULTI-STORE • CLOUD-BASED
              </Typography>
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  mb: 1.5,
                  fontSize: { xs: '1.85rem', sm: '2.3rem', md: '2.8rem' },
                  lineHeight: 1.2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  position: 'relative',
                }}
              >
                {t('hero_title')}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                  lineHeight: 1.7,
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                }}
              >
                {t('hero_subtitle')}
              </Typography>

              <Box sx={{ mb: 2.5 }}>
                {features.map((feature, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 1,
                      animation: `fadeInLeft ${0.5 + index * 0.1}s ease-out`,
                      '@keyframes fadeInLeft': {
                        '0%': { opacity: 0, transform: 'translateX(-20px)' },
                        '100%': { opacity: 1, transform: 'translateX(0)' },
                      },
                    }}
                  >
                    <CheckCircleIcon 
                      sx={{ 
                        color: theme.palette.success.main, 
                        mr: 2, 
                        fontSize: 24,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                      }} 
                    />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                        fontSize: '1.05rem',
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={onLoginClick}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    px: 5,
                    py: 2,
                    borderRadius: 3,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.4)}`,
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: `0 15px 40px ${alpha(theme.palette.primary.main, 0.5)}`,
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {t('get_started')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<PlayCircleOutlineIcon />}
                  onClick={() => {
                    const demoSection = document.getElementById('demo');
                    if (demoSection) {
                      demoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  sx={{
                    px: 5,
                    py: 2,
                    borderRadius: 3,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderWidth: 2,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      borderWidth: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      transform: 'translateY(-3px)',
                      boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {t('request_demo')}
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                height: { xs: 420, sm: 480, md: 560 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'fadeIn 1.5s ease-out',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0 },
                  '100%': { opacity: 1 },
                },
              }}
            >
              {/* Main dashboard mockup card */}
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: '88%', md: '82%' },
                  borderRadius: 4,
                  p: { xs: 2.5, md: 3 },
                  background: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.9)
                    : '#fff',
                  boxShadow: `0 30px 70px ${alpha(theme.palette.primary.main, 0.25)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                  backdropFilter: 'blur(10px)',
                  animation: 'floatCard 6s ease-in-out infinite',
                  '@keyframes floatCard': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-16px)' },
                  },
                }}
              >
                {/* Mockup header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        color: '#fff',
                      }}
                    >
                      <ReceiptLongIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.1 }}>
                        Billeur
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.secondary }}>
                        Dashboard
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.7 }}>
                    {[theme.palette.error.main, theme.palette.warning.main, theme.palette.success.main].map((c, i) => (
                      <Box key={i} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: alpha(c, 0.7) }} />
                    ))}
                  </Box>
                </Box>

                {/* Revenue summary row */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      p: 1.5,
                      borderRadius: 2.5,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.68rem', color: theme.palette.text.secondary, fontWeight: 600 }}>
                      Revenue
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: theme.palette.primary.main }}>
                      €48.2k
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      p: 1.5,
                      borderRadius: 2.5,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.68rem', color: theme.palette.text.secondary, fontWeight: 600 }}>
                      Invoices
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: theme.palette.success.main }}>
                      1,284
                    </Typography>
                  </Box>
                </Box>

                {/* Mini bar chart */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: 1,
                    height: 120,
                    px: 0.5,
                  }}
                >
                  {[55, 80, 45, 95, 65, 88, 72].map((h, i) => (
                    <Box
                      key={i}
                      sx={{
                        flex: 1,
                        height: `${h}%`,
                        borderRadius: 1.5,
                        background: i % 2 === 0
                          ? `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.4)} 100%)`
                          : `linear-gradient(180deg, ${theme.palette.secondary.main} 0%, ${alpha(theme.palette.secondary.main, 0.4)} 100%)`,
                        animation: `growBar 1.2s ease-out ${i * 0.1}s both`,
                        '@keyframes growBar': {
                          '0%': { transform: 'scaleY(0)', transformOrigin: 'bottom' },
                          '100%': { transform: 'scaleY(1)', transformOrigin: 'bottom' },
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Floating badge: PEPPOL compliant */}
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: 8, md: 24 },
                  right: { xs: 4, md: 0 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: 3,
                  bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
                  boxShadow: `0 12px 30px ${alpha(theme.palette.success.main, 0.25)}`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                  animation: 'floatBadge 5s ease-in-out infinite',
                  '@keyframes floatBadge': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-12px)' },
                  },
                }}
              >
                <VerifiedIcon sx={{ color: theme.palette.success.main, fontSize: 22 }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.1 }}>
                    PEPPOL
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: theme.palette.text.secondary }}>
                    Compliant
                  </Typography>
                </Box>
              </Box>

              {/* Floating badge: Growth */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: { xs: 12, md: 40 },
                  left: { xs: 2, md: -10 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: 3,
                  bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
                  boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.25)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                  animation: 'floatBadge2 6s ease-in-out infinite',
                  '@keyframes floatBadge2': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(14px)' },
                  },
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.1, color: theme.palette.success.main }}>
                    +27%
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: theme.palette.text.secondary }}>
                    This month
                  </Typography>
                </Box>
              </Box>

              {/* Floating badge: Wallet (top-left, subtle) */}
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: 4, md: 8 },
                  left: { xs: 4, md: 6 },
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
                  boxShadow: `0 10px 26px ${alpha(theme.palette.secondary.main, 0.25)}`,
                  color: theme.palette.secondary.main,
                  animation: 'floatBadge 7s ease-in-out infinite',
                }}
              >
                <AccountBalanceWalletIcon sx={{ fontSize: 24 }} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection;
